import { defineStore } from 'pinia'
import { AudienceRuntimeLoader } from '@/core/release/runtimeLoader'
import { adaptReleaseContent } from '@/adapters/release/adaptReleaseContent'
import { normalizeLegacyStableIds } from '@/adapters/legacy/stableIds'
import { isPreviewEntry, PreviewBridge, resolvePreviewOrigin } from '@/core/preview/previewBridge'
import type { Destination, EntryRouteVariant, Gate, JourneyMode, JourneyOrigin, LegacyAudienceConfig, Transport, Zone } from '@/domain/audience/types'
import type { AudienceCapabilities, PreviewReleasePayload, PreviewScene, ResolvedAudienceSkinV1 } from '@/domain/release/types'
import { createSharedRouteHash, createSharedRouteId, parseSharedRoute } from '@/features/route/routeLink'
import { audienceEnvironment } from '@/core/runtime/environment'
import { applyResolvedTheme, createResolvedTheme } from '@/shared/theme/theme'
import type { ValidationSkinName } from '@/shared/theme/fixtures'

const LANGUAGE_STORAGE_KEY = 'nanchang-h5-language-v1'
const LAST_ROUTE_STORAGE_KEY = 'venue-audience-last-route-v1'
let loadedRuntimeSkin: ResolvedAudienceSkinV1 | null = null
let loadedRuntimeSkins: Record<string, ResolvedAudienceSkinV1> = {}
let loadedSkinContext: { stadiumId: string; eventId: string; defaultSkinId: string } | null = null

interface AudienceState {
  status: 'idle' | 'loading' | 'ready' | 'error'
  error: string
  stadiumId: string
  releaseId: string
  config: LegacyAudienceConfig | null
  locale: 'zh-CN' | 'en'
  mode: JourneyMode
  transportId: string
  originId: string
  gateId: string
  destinationId: string
  destinationIndex: number
  zoneId: string
  lastRoute: JourneySnapshot | null
  previewMode: boolean
  runtimeState: 'online' | 'cached' | 'preview' | null
  runtimeVerifiedAt: string
  runtimeOfflineValidUntil: string
  capabilities: AudienceCapabilities
  activeSkinId: string
  skinOptions: Array<{ skinId: string; name: string }>
}

export interface JourneySnapshot {
  mode: JourneyMode
  transportId: string
  originId: string
  gateId: string
  destinationId: string
  zoneId: string
}

interface StoredLastRouteV1 {
  schemaVersion: 'venue-audience-last-route/v1'
  version: 1
  stadiumId: string
  eventId: string
  releaseId: string
  routeId: string
  journey: JourneySnapshot
  savedAt: string
}

export const useAudienceStore = defineStore('audience', {
  state: (): AudienceState => ({
    status: 'idle', error: '', stadiumId: '', releaseId: '', config: null, locale: 'zh-CN', mode: 'entry',
    transportId: '', originId: '', gateId: '', destinationId: '', destinationIndex: -1, zoneId: '', lastRoute: null,
    previewMode: false,
    runtimeState: null, runtimeVerifiedAt: '', runtimeOfflineValidUntil: '', capabilities: { broadcast: false, panorama: false },
    activeSkinId: '', skinOptions: [],
  }),
  getters: {
    transports: (state): Transport[] => [...(state.config?.transports?.items || [])]
      .filter((item) => item.enabled !== false)
      .sort((left, right) => (left.order || 0) - (right.order || 0)),
    selectedTransport(): Transport | undefined { return this.transports.find((item) => item.id === this.transportId) },
    origins(): JourneyOrigin[] { return entryOrigins(this.selectedTransport) },
    selectedOrigin(): JourneyOrigin | undefined { return this.origins.find((item) => item.id === this.originId) },
    gates(): Gate[] { return this.selectedTransport?.entry?.gates || [] },
    selectedEntryRoute(): EntryRouteVariant | undefined {
      return (this.selectedTransport?.entry?.routes || []).find((item) => item.originId === this.originId && item.gateId === this.gateId)
    },
    destinations(): Destination[] { return (this.selectedTransport?.exit?.locations || []).filter((item) => item.enabled !== false) },
    selectedDestination(): Destination | undefined { return this.destinations.find((item) => item.id === this.destinationId) },
    visibleZones(): Zone[] {
      const zones = this.config?.zones?.items || []
      if (!this.transportId) return zones
      return zones.filter((zone) => {
        const bindings = zone.entryBindings || []
        return !bindings.length || bindings.some((binding) => binding.transportId === this.transportId && (!binding.gateId || binding.gateId === this.gateId))
      })
    },
    selectedZone(): Zone | undefined { return (this.config?.zones?.items || []).find((item) => item.id === this.zoneId) },
    allowedAssetUrls(): Set<string> { return new Set(this.config?._runtime?.allowedAssetUrls || []) },
  },
  actions: {
    async bootstrap() {
      if (this.status === 'loading' || this.status === 'ready') return
      if (isPreviewEntry()) { await this.bootstrapPreview(); return }
      const params = new URLSearchParams(window.location.search)
      const stadiumId = params.get('s')?.trim() || ''
      if (!stadiumId) { this.fail('缺少场馆参数'); return }
      if (!supportsRuntime()) { this.fail('当前浏览器缺少安全校验或离线缓存能力，请升级微信或浏览器'); return }
      this.status = 'loading'
      this.stadiumId = stadiumId
      try {
        const result = await new AudienceRuntimeLoader().load(stadiumId)
        if (!('bundle' in result)) { this.fail(result.reason); return }
        this.config = result.bundle.config
        this.releaseId = result.bundle.releaseId
        this.runtimeState = result.state === 'ready-cached' ? 'cached' : 'online'
        this.runtimeVerifiedAt = result.bundle.verifiedAt
        this.runtimeOfflineValidUntil = result.bundle.offlineValidUntil
        this.capabilities = result.bundle.capabilities || { broadcast: false, panorama: false }
        loadedRuntimeSkin = result.bundle.skin
        loadedRuntimeSkins = result.bundle.skins || {}
        const defaultSkinId = result.bundle.defaultSkinId || ''
        loadedSkinContext = defaultSkinId ? { stadiumId, eventId: result.bundle.eventId, defaultSkinId } : null
        this.skinOptions = result.bundle.skinOptions || []
        const requestedPublishedSkin = params.get('skinEvent') === result.bundle.eventId ? params.get('skin') : null
        const selected = resolvePublishedSkinSelection(stadiumId, result.bundle.eventId, defaultSkinId, loadedRuntimeSkins, requestedPublishedSkin)
        this.activeSkinId = selected.skinId
        const activeSkin = selected.skin
          || (audienceEnvironment.showDemoSkinSwitcher ? await resolveDemoSkin(result.bundle.skin, params.get('demoSkin')) : result.bundle.skin)
        applyResolvedTheme(createResolvedTheme(activeSkin))
        const requestedLanguage = params.get('lang')
        let storedLanguage = ''
        try { storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) || '' } catch { /* Keep default. */ }
        this.setLocale(this.config.settings?.versionMode === 'international' && (requestedLanguage === 'en' || (!requestedLanguage && storedLanguage === 'en')) ? 'en' : 'zh-CN')
        this.restoreJourney()
        this.loadLastRoute()
        this.status = 'ready'
      } catch (error) { this.fail(error instanceof Error ? error.message : '活动内容加载失败') }
    },
    async retryBootstrap() {
      if (this.status === 'loading') return
      loadedRuntimeSkin = null
      loadedRuntimeSkins = {}
      loadedSkinContext = null
      this.activeSkinId = ''; this.skinOptions = []
      this.status = 'idle'; this.error = ''; this.config = null; this.releaseId = ''
      await this.bootstrap()
    },
    async bootstrapPreview() {
      const allowedOrigin = resolvePreviewOrigin()
      if (!allowedOrigin || window.parent === window) { this.fail('预览必须运行在受信任的 VenueFlow iframe 中'); return }
      this.status = 'loading'; this.previewMode = true; this.stadiumId = 'preview'
      previewBridge?.stop()
      previewBridge = new PreviewBridge({
        allowedOrigin,
        parentWindow: window.parent,
        onApply: async (payload) => this.applyPreview(payload),
        onLocale: (locale) => this.setLocale(locale === 'en-US' ? 'en' : 'zh-CN'),
        onScene: (scene) => navigatePreviewScene(scene),
      })
      previewBridge.start()
    },
    async applyPreview(payload: PreviewReleasePayload) {
      const capabilities = { broadcast: false, panorama: !payload.disabledCapabilities.includes('network') }
      const config = await normalizeLegacyStableIds(await adaptReleaseContent(payload.content, null, payload.i18n, { skin: payload.skin, capabilities }))
      config._runtime = { stadiumId: 'preview', eventId: 'preview', releaseId: `preview-${payload.previewRevision}`, source: 'preview', allowedAssetUrls: [], capabilities }
      this.config = config; this.releaseId = `preview-${payload.previewRevision}`; this.status = 'ready'; this.error = ''
      this.runtimeState = 'preview'; this.runtimeVerifiedAt = ''; this.runtimeOfflineValidUntil = ''; this.capabilities = capabilities
      this.activeSkinId = ''; this.skinOptions = []; loadedRuntimeSkins = {}; loadedSkinContext = null
      applyResolvedTheme(createResolvedTheme(payload.skin))
      this.setLocale(payload.locale === 'en-US' ? 'en' : 'zh-CN')
      navigatePreviewScene(payload.scene)
    },
    fail(message: string) { this.status = 'error'; this.error = message },
    setLocale(locale: 'zh-CN' | 'en') {
      this.locale = this.config?.settings?.versionMode === 'international' && locale === 'en' ? 'en' : 'zh-CN'
      document.documentElement.lang = this.locale
      if (!this.previewMode) try { localStorage.setItem(LANGUAGE_STORAGE_KEY, this.locale === 'en' ? 'en' : 'zh') } catch { /* Best effort. */ }
    },
    async setDemoSkin(requestedSkin: string) {
      if (!audienceEnvironment.showDemoSkinSwitcher || !loadedRuntimeSkin || this.previewMode || this.skinOptions.length) return
      applyResolvedTheme(createResolvedTheme(await resolveDemoSkin(loadedRuntimeSkin, requestedSkin)))
      const nextUrl = new URL(window.location.href)
      if (requestedSkin) nextUrl.searchParams.set('demoSkin', requestedSkin)
      else nextUrl.searchParams.delete('demoSkin')
      nextUrl.searchParams.set('lang', this.locale === 'en' ? 'en' : 'zh')
      window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`)
    },
    setPublishedSkin(requestedSkinId: string) {
      if (this.previewMode || !loadedSkinContext) return
      const skin = loadedRuntimeSkins[requestedSkinId]
      if (!skin) return
      this.activeSkinId = requestedSkinId
      applyResolvedTheme(createResolvedTheme(skin))
      try { localStorage.setItem(activitySkinCacheKey(loadedSkinContext.stadiumId, loadedSkinContext.eventId), requestedSkinId) } catch { /* Best effort. */ }
      const nextUrl = new URL(window.location.href)
      nextUrl.searchParams.set('skin', requestedSkinId)
      nextUrl.searchParams.set('skinEvent', loadedSkinContext.eventId)
      nextUrl.searchParams.delete('demoSkin')
      nextUrl.searchParams.set('lang', this.locale === 'en' ? 'en' : 'zh')
      window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`)
    },
    beginJourney(mode: JourneyMode) {
      this.mode = mode; this.transportId = ''; this.originId = ''; this.gateId = ''; this.setDestination(''); this.zoneId = ''; this.persistJourney()
    },
    selectTransport(id: string) {
      this.transportId = id; this.originId = ''; this.gateId = ''; this.setDestination(''); this.zoneId = ''
      const transport = this.transports.find((item) => item.id === id)
      const gates = transport?.entry?.gates || []
      if (this.mode === 'entry' && !entryOrigins(transport).length && gates.length === 1) this.gateId = gates[0]?.id || ''
      const destinations = (transport?.exit?.locations || []).filter((item) => item.enabled !== false)
      if (this.mode === 'exit' && destinations.length === 1) this.setDestination(destinations[0]?.id || '')
      this.persistJourney()
    },
    selectOrigin(id: string) { this.originId = this.originId === id ? '' : id; this.zoneId = ''; this.persistJourney() },
    selectGate(id: string) { this.gateId = this.gateId === id ? '' : id; this.zoneId = ''; this.persistJourney() },
    selectDestination(value: number | string) {
      const destination = typeof value === 'number' ? this.destinations[value] : this.destinations.find((item) => item.id === value)
      this.setDestination(destination?.id || '')
      this.persistJourney()
    },
    setDestination(id: string) {
      this.destinationId = id
      this.destinationIndex = id ? this.destinations.findIndex((item) => item.id === id) : -1
    },
    selectZone(id: string) { this.zoneId = id; this.persistJourney() },
    snapshot(): JourneySnapshot {
      return { mode: this.mode, transportId: this.transportId, originId: this.originId, gateId: this.gateId, destinationId: this.destinationId, zoneId: this.zoneId }
    },
    persistJourney() {
      if (!this.stadiumId || this.previewMode) return
      try { sessionStorage.setItem(`venue-h5-audience-v2-journey:${this.stadiumId}`, JSON.stringify({ version: 3, ...this.snapshot() })) } catch { /* Best effort. */ }
    },
    restoreJourney() {
      if (!this.stadiumId || this.previewMode) return
      try {
        const stored = JSON.parse(sessionStorage.getItem(`venue-h5-audience-v2-journey:${this.stadiumId}`) || 'null') as Record<string, unknown> | null
        const snapshot = this.snapshotFromUnknown(stored, true)
        if (snapshot) this.applySnapshot(snapshot)
      } catch { /* Ignore invalid state. */ }
    },
    lastRouteStorageKey() { return LAST_ROUTE_STORAGE_KEY },
    loadLastRoute() {
      if (this.previewMode) return
      const eventId = this.config?._runtime?.eventId || ''
      try {
        const current = JSON.parse(localStorage.getItem(LAST_ROUTE_STORAGE_KEY) || 'null') as Partial<StoredLastRouteV1> | null
        if (current?.schemaVersion === 'venue-audience-last-route/v1' && current.version === 1
          && current.stadiumId === this.stadiumId && current.eventId === eventId && current.journey) {
          const snapshot = this.snapshotFromUnknown(current.journey)
          if (snapshot && (current.routeId === legacyStoredRouteId(snapshot)
            || current.routeId === createSharedRouteId(snapshot, eventId))) { this.lastRoute = snapshot; return }
        }
        const legacyKey = `venue-h5-last-route-v1:${eventId || this.stadiumId}`
        const legacy = JSON.parse(localStorage.getItem(legacyKey) || 'null') as Record<string, unknown> | null
        const migrated = this.snapshotFromUnknown(legacy)
        if (migrated) { this.lastRoute = migrated; this.writeLastRoute(migrated) }
      } catch { /* Ignore invalid local history. */ }
    },
    persistLastRoute() {
      const value = this.validatedSnapshot(this.snapshot())
      if (!value) return
      this.lastRoute = value
      this.writeLastRoute(value)
    },
    writeLastRoute(value: JourneySnapshot) {
      if (this.previewMode) return
      const eventId = this.config?._runtime?.eventId || ''
      if (!eventId) return
      const stored: StoredLastRouteV1 = {
        schemaVersion: 'venue-audience-last-route/v1', version: 1, stadiumId: this.stadiumId, eventId,
        releaseId: this.releaseId, routeId: createSharedRouteId(value, eventId), journey: value, savedAt: new Date().toISOString(),
      }
      try { localStorage.setItem(LAST_ROUTE_STORAGE_KEY, JSON.stringify(stored)) } catch { /* Best effort. */ }
    },
    restoreLastRoute() {
      if (!this.lastRoute) return false
      this.applySnapshot(this.lastRoute); this.persistJourney(); return true
    },
    sharedRouteHash(value?: JourneySnapshot) {
      const snapshot = this.validatedSnapshot(value || this.snapshot())
      const eventId = this.config?._runtime?.eventId || ''
      return snapshot && eventId ? createSharedRouteHash({ ...snapshot, destinationIndex: this.destinationIndex }, eventId, this.releaseId) : ''
    },
    restoreSharedRoute(routeIdValue: string, query: Record<string, unknown>, search = window.location.search) {
      const eventId = this.config?._runtime?.eventId || ''
      const shared = parseSharedRoute(routeIdValue, query, eventId, search)
      const snapshot = this.snapshotFromUnknown(shared)
      if (!snapshot) return null
      this.applySnapshot(snapshot); this.lastRoute = snapshot; this.persistJourney(); this.writeLastRoute(snapshot)
      return snapshot
    },
    applySnapshot(value: JourneySnapshot) {
      this.mode = value.mode; this.transportId = value.transportId; this.originId = value.originId; this.gateId = value.gateId; this.zoneId = value.zoneId; this.setDestination(value.destinationId)
    },
    snapshotFromUnknown(value: unknown, allowPartial = false): JourneySnapshot | null {
      if (!isRecord(value) || (value.mode !== 'entry' && value.mode !== 'exit')) return null
      const stored = value
      const transportId = string(stored.transportId) || string(stored.transport)
      const transport = this.transports.find((item) => item.id === transportId)
      if (!transport) return null
      let destinationId = string(stored.destinationId)
      if (!destinationId && Number.isInteger(stored.destinationIndex)) destinationId = (transport.exit?.locations || [])[Number(stored.destinationIndex)]?.id || ''
      return this.validatedSnapshot({
        mode: stored.mode as JourneyMode, transportId, originId: string(stored.originId) || string(stored.origin), gateId: string(stored.gateId) || string(stored.entryGate),
        destinationId, zoneId: string(stored.zoneId) || string(stored.zone),
      }, allowPartial)
    },
    validatedSnapshot(value: JourneySnapshot, allowPartial = false): JourneySnapshot | null {
      const transport = this.transports.find((item) => item.id === value.transportId)
      if (!transport) return null
      if (value.mode === 'entry') {
        const origins = entryOrigins(transport)
        const origin = origins.find((item) => item.id === value.originId)
        const gates = transport.entry?.gates || []
        const gate = gates.find((item) => item.id === value.gateId) || (!origins.length && gates.length === 1 ? gates[0] : undefined)
        const routes = transport.entry?.routes || []
        const route = routes.find((item) => item.originId === origin?.id && item.gateId === gate?.id)
        const zone = (this.config?.zones?.items || []).find((item) => item.id === value.zoneId)
        const matches = zone && (!(zone.entryBindings || []).length || zone.entryBindings?.some((binding) => binding.transportId === transport.id && (!binding.gateId || binding.gateId === gate?.id)))
        if (value.originId && !origin) return null
        if (value.gateId && !gate) return null
        if (value.zoneId && (!zone || !matches)) return null
        if (!allowPartial && (!zone || !matches || (origins.length && !origin) || (gates.length > 0 && !gate) || (routes.length > 0 && !route))) return null
        return { ...value, originId: origin?.id || '', gateId: gate?.id || '', zoneId: zone?.id || '', destinationId: '' }
      }
      const destinations = (transport.exit?.locations || []).filter((item) => item.enabled !== false)
      const destination = destinations.find((item) => item.id === value.destinationId) || (destinations.length === 1 ? destinations[0] : undefined)
      if (value.destinationId && !destination) return null
      if (!allowPartial && !destination && destinations.length) return null
      return { ...value, originId: '', gateId: '', zoneId: '', destinationId: destination?.id || '' }
    },
  },
})

let previewBridge: PreviewBridge | null = null

function navigatePreviewScene(scene: PreviewScene) {
  const paths: Record<PreviewScene, string> = {
    home: '/home', entry: '/entry', route: '/entry/transports', services: '/services', broadcast: '/broadcasts', annotation: '/services', vr: '/mode',
  }
  const target = paths[scene]
  if (window.location.hash !== `#${target}`) window.location.hash = target
}

function legacyStoredRouteId(value: JourneySnapshot): string {
  return value.mode === 'entry'
    ? `entry:${value.transportId}:${value.gateId}:${value.zoneId}`
    : `exit:${value.transportId}:${value.destinationId}`
}
function entryOrigins(transport: Transport | undefined): JourneyOrigin[] {
  return [...(transport?.entry?.origins || [])]
    .filter((item) => item.enabled !== false)
    .sort((left, right) => (left.order || 0) - (right.order || 0))
}
function string(value: unknown): string { return typeof value === 'string' ? value : '' }
function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === 'object' && value !== null && !Array.isArray(value) }
function supportsRuntime(): boolean {
  return typeof fetch === 'function' && typeof indexedDB !== 'undefined' && typeof crypto?.subtle?.digest === 'function' && typeof TextEncoder === 'function'
}

async function resolveDemoSkin(defaultSkin: ResolvedAudienceSkinV1, requestedSkin: string | null): Promise<ResolvedAudienceSkinV1> {
  const demoSkins: ValidationSkinName[] = ['wayfinding', 'sport', 'performance', 'mist', 'pitch', 'champion', 'homefire', 'production']
  if (!requestedSkin || !demoSkins.includes(requestedSkin as ValidationSkinName)) return defaultSkin
  const { createValidationSkin } = await import('@/shared/theme/fixtures')
  return createValidationSkin(requestedSkin as ValidationSkinName)
}

export function resolvePublishedSkinSelection(stadiumId: string, eventId: string, defaultSkinId: string, skins: Record<string, ResolvedAudienceSkinV1>, requestedSkinId: string | null): { skinId: string; skin: ResolvedAudienceSkinV1 | null } {
  if (!defaultSkinId || !skins[defaultSkinId]) return { skinId: '', skin: null }
  const requestedSkin = requestedSkinId ? skins[requestedSkinId] : undefined
  if (requestedSkinId && requestedSkin) return { skinId: requestedSkinId, skin: requestedSkin }
  let cachedSkinId = ''
  try { cachedSkinId = localStorage.getItem(activitySkinCacheKey(stadiumId, eventId)) || '' } catch { /* Use default. */ }
  const cachedSkin = cachedSkinId ? skins[cachedSkinId] : undefined
  if (cachedSkinId && cachedSkin) return { skinId: cachedSkinId, skin: cachedSkin }
  return { skinId: defaultSkinId, skin: skins[defaultSkinId] || null }
}

export function activitySkinCacheKey(stadiumId: string, eventId: string): string {
  return `venue-audience-skin-choice:v1:${stadiumId}:${eventId}`
}
